export const ACCOUNT_TOKEN_KEY = "mindovia_account_token";
export const ACCOUNT_RETURN_INTENT_KEY = "mindovia_account_return_intent";
export const PENDING_CLAIM_PREFIX = "mindovia_pending_claim:";

function getSessionStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function normalizedCode(anonymousCode) {
  return String(anonymousCode || "").trim();
}

export function pendingClaimKey(anonymousCode) {
  return `${PENDING_CLAIM_PREFIX}${normalizedCode(anonymousCode)}`;
}

export function getAccountToken() {
  try {
    return getSessionStorage()?.getItem(ACCOUNT_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setAccountToken(token) {
  const value = String(token || "").trim();
  if (!value) return false;
  try {
    const storage = getSessionStorage();
    if (!storage) return false;
    storage.setItem(ACCOUNT_TOKEN_KEY, value);
    return true;
  } catch {
    return false;
  }
}

export function clearAccountToken() {
  try {
    getSessionStorage()?.removeItem(ACCOUNT_TOKEN_KEY);
  } catch {}
}

export function storePendingClaim(anonymousCode, claimToken) {
  const code = normalizedCode(anonymousCode);
  const token = String(claimToken || "").trim();
  if (!code || !token) return false;
  try {
    const storage = getSessionStorage();
    if (!storage) return false;
    storage.setItem(
      pendingClaimKey(code),
      JSON.stringify({ claim_token: token, created_at: new Date().toISOString() }),
    );
    return true;
  } catch {
    return false;
  }
}

export function getPendingClaim(anonymousCode) {
  const code = normalizedCode(anonymousCode);
  if (!code) return null;
  try {
    const raw = getSessionStorage()?.getItem(pendingClaimKey(code));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.claim_token !== "string" || !parsed.claim_token) {
      removePendingClaim(code);
      return null;
    }
    return {
      anonymousCode: code,
      claimToken: parsed.claim_token,
      createdAt: typeof parsed.created_at === "string" ? parsed.created_at : null,
    };
  } catch {
    removePendingClaim(code);
    return null;
  }
}

export function removePendingClaim(anonymousCode) {
  const code = normalizedCode(anonymousCode);
  if (!code) return;
  try {
    getSessionStorage()?.removeItem(pendingClaimKey(code));
  } catch {}
}

export function safeReturnPath(candidate, fallback = "/account/results") {
  const value = typeof candidate === "string" ? candidate.trim() : "";
  if (!value.startsWith("/")) return fallback;
  try {
    const localOrigin = "https://mindovia.invalid";
    return new URL(value, localOrigin).origin === localOrigin ? value : fallback;
  } catch {
    return fallback;
  }
}

export function setAccountReturnIntent({ returnTo, claimCode = null } = {}) {
  const intent = {
    returnTo: safeReturnPath(returnTo),
    claimCode: normalizedCode(claimCode) || null,
  };
  try {
    getSessionStorage()?.setItem(ACCOUNT_RETURN_INTENT_KEY, JSON.stringify(intent));
    return intent;
  } catch {
    return intent;
  }
}

export function getAccountReturnIntent() {
  try {
    const raw = getSessionStorage()?.getItem(ACCOUNT_RETURN_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      returnTo: safeReturnPath(parsed?.returnTo),
      claimCode: normalizedCode(parsed?.claimCode) || null,
    };
  } catch {
    clearAccountReturnIntent();
    return null;
  }
}

export function clearAccountReturnIntent() {
  try {
    getSessionStorage()?.removeItem(ACCOUNT_RETURN_INTENT_KEY);
  } catch {}
}
